import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './platform-stats-ops-workers.scss';

type OpsRole = 'ops_worker' | 'ops_admin';

interface OpsCompany {
  id: number;
  name: string | null;
  opsUserCount: number;
}

interface OpsWorker {
  id: string;
  name: string | null;
  email: string | null;
  role: OpsRole | null;
  languages: string[];
  companyId: number | null;
  companyName: string | null;
  createdAt: string | null;
}

interface ListResponse {
  success: boolean;
  opsCompany: OpsCompany | null;
  workers: OpsWorker[];
}

interface CreateResponse {
  success: boolean;
  worker: {
    userId: string;
    name: string;
    email: string;
    role: OpsRole;
    companyId: number;
    companyName: string | null;
    languages: string[];
  };
  password: string;
  passwordLink: string | null;
}

interface RemoveResponse {
  success: boolean;
  removed: { userId: string; name: string | null; auth0Blocked: boolean };
}

const ROLE_LABELS: Record<OpsRole, string> = {
  ops_worker: 'Worker',
  ops_admin: 'Admin',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const errorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as { response?: { data?: { error?: string } } };
  return axiosErr?.response?.data?.error || fallback;
};

const CopyButton: FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — user can still select the text
    }
  };
  return (
    <button type='button' className='copy-btn' onClick={copy}>
      {copied ? 'Copied' : label}
    </button>
  );
};

/**
 * Admin "Ops Workers" tab: lists the internal ops accounts and lets a
 * platform-stats admin provision a new one (Auth0 user + users row +
 * completed onboarding), toggle worker/admin, or remove an account (ops
 * access revoked + Auth0 login blocked). The generated password is shown
 * exactly once.
 */
const PlatformStatsOpsWorkers: FC = () => {
  const { getAccessTokenSilently, user: currentUser } = useAuth0();

  const [opsCompany, setOpsCompany] = useState<OpsCompany | null>(null);
  const [workers, setWorkers] = useState<OpsWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateResponse | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{
    id: string;
    message: string;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<ListResponse>('/platform-stats/ops-workers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setOpsCompany(res.data.opsCompany);
        setWorkers(res.data.workers);
      }
    } catch (err) {
      setListError(errorMessage(err, 'Failed to load ops workers'));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    load();
  }, [load]);

  const authHeaders = async () => ({
    headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
  });

  const toggleRole = async (w: OpsWorker) => {
    if (busyId) return;
    const makeAdmin = w.role !== 'ops_admin';
    setBusyId(w.id);
    setRowError(null);
    setNotice(null);
    try {
      await api.patch(
        `/platform-stats/ops-workers/${encodeURIComponent(w.id)}`,
        { isAdmin: makeAdmin },
        await authHeaders(),
      );
      setNotice(
        `${w.name ?? w.id} is now an ${makeAdmin ? 'ops admin' : 'ops worker'}.`,
      );
      await load();
    } catch (err) {
      setRowError({
        id: w.id,
        message: errorMessage(err, 'Failed to update role'),
      });
    } finally {
      setBusyId(null);
    }
  };

  const removeWorker = async (w: OpsWorker) => {
    if (busyId) return;
    setBusyId(w.id);
    setRowError(null);
    setNotice(null);
    try {
      const res = await api.delete<RemoveResponse>(
        `/platform-stats/ops-workers/${encodeURIComponent(w.id)}`,
        await authHeaders(),
      );
      setConfirmRemoveId(null);
      setNotice(
        res.data.removed.auth0Blocked
          ? `${w.name ?? w.id} removed — ops access revoked and login blocked.`
          : `${w.name ?? w.id} removed — ops access revoked, but the Auth0 login could not be blocked. Block it manually in Auth0.`,
      );
      await load();
    } catch (err) {
      setRowError({
        id: w.id,
        message: errorMessage(err, 'Failed to remove ops worker'),
      });
    } finally {
      setBusyId(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<CreateResponse>(
        '/platform-stats/ops-workers',
        { name: name.trim(), email: email.trim(), isAdmin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCreated(res.data);
      setName('');
      setEmail('');
      setIsAdmin(false);
      await load();
    } catch (err) {
      setFormError(errorMessage(err, 'Failed to create ops worker'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='ops-workers'>
      <div className='ops-workers-grid'>
        <section className='ops-workers-card create-card'>
          <h3>New ops worker</h3>
          <p className='card-hint'>
            Creates the Auth0 login, the users row (internal, ops) and a
            completed onboarding so they skip onboarding and can open /tasks.
            {opsCompany && (
              <>
                {' '}
                New workers join <strong>
                  {opsCompany.name ?? 'company'}
                </strong>{' '}
                (ID {opsCompany.id}).
              </>
            )}
          </p>

          {created ? (
            <div className='created-panel' role='status'>
              <h4>
                {ROLE_LABELS[created.worker.role]} account created for{' '}
                {created.worker.name}
              </h4>
              <dl>
                <dt>Email</dt>
                <dd>
                  <code>{created.worker.email}</code>
                  <CopyButton value={created.worker.email} label='Copy' />
                </dd>
                <dt>Password</dt>
                <dd>
                  <code className='password'>{created.password}</code>
                  <CopyButton value={created.password} label='Copy' />
                </dd>
                {created.passwordLink && (
                  <>
                    <dt>One-time link</dt>
                    <dd>
                      <a
                        href={created.passwordLink}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {created.passwordLink}
                      </a>
                      <CopyButton value={created.passwordLink} label='Copy' />
                    </dd>
                  </>
                )}
                <dt>User ID</dt>
                <dd>
                  <code>{created.worker.userId}</code>
                </dd>
              </dl>
              <p className='created-warning'>
                Share the password now — it is not stored and will not be shown
                again.
              </p>
              <button
                type='button'
                className='primary-btn'
                onClick={() => setCreated(null)}
              >
                Create another
              </button>
            </div>
          ) : (
            <form className='create-form' onSubmit={submit}>
              <label>
                Name
                <input
                  type='text'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='Jane Doe'
                  required
                  autoComplete='off'
                />
              </label>
              <label>
                Email
                <input
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='jane@example.com'
                  required
                  autoComplete='off'
                />
              </label>
              <label className='checkbox'>
                <input
                  type='checkbox'
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                />
                Admin
                <span className='checkbox-hint'>
                  {isAdmin
                    ? 'ops_admin — sees all tasks, can dispatch'
                    : 'ops_worker — online / phone tasks only'}
                </span>
              </label>
              {formError && (
                <div className='form-error' role='alert'>
                  {formError}
                </div>
              )}
              <button
                type='submit'
                className='primary-btn'
                disabled={submitting || !name.trim() || !email.trim()}
              >
                {submitting
                  ? 'Creating…'
                  : `Create ${isAdmin ? 'ops admin' : 'ops worker'}`}
              </button>
            </form>
          )}
        </section>

        <section className='ops-workers-card list-card'>
          <div className='list-header'>
            <h3>Ops workers</h3>
            <span className='list-count'>
              {loading ? 'Loading…' : `${workers.length} accounts`}
            </span>
          </div>
          {listError && (
            <div className='form-error' role='alert'>
              {listError}
            </div>
          )}
          {notice && (
            <div className='list-notice' role='status'>
              {notice}
            </div>
          )}
          <div className='ops-workers-table-wrap'>
            <table className='ops-workers-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Languages</th>
                  <th>Company</th>
                  <th>Created</th>
                  <th className='actions-col'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && workers.length === 0 && (
                  <tr>
                    <td className='empty' colSpan={7}>
                      No ops workers yet
                    </td>
                  </tr>
                )}
                {workers.map((w) => {
                  const isSelf = currentUser?.sub === w.id;
                  const busy = busyId === w.id;
                  const confirming = confirmRemoveId === w.id;
                  return (
                    <React.Fragment key={w.id}>
                      <tr className={confirming ? 'confirming' : undefined}>
                        <td>
                          <div className='user-cell'>
                            <span>
                              {w.name ?? '—'}
                              {isSelf && <span className='you-tag'>you</span>}
                            </span>
                            <span className='user-id'>{w.id}</span>
                          </div>
                        </td>
                        <td>{w.email ?? '—'}</td>
                        <td>
                          {w.role ? (
                            <span className={`role-chip ${w.role}`}>
                              {ROLE_LABELS[w.role]}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {w.languages.length ? w.languages.join(', ') : '—'}
                        </td>
                        <td>
                          {w.companyName ?? '—'}
                          {w.companyId != null && (
                            <span className='user-id'> #{w.companyId}</span>
                          )}
                        </td>
                        <td>{formatDate(w.createdAt)}</td>
                        <td className='actions-col'>
                          {confirming ? (
                            <div className='row-actions'>
                              <span className='confirm-text'>
                                Remove {w.name ?? w.id}?
                              </span>
                              <button
                                type='button'
                                className='danger-btn'
                                disabled={busy}
                                onClick={() => removeWorker(w)}
                              >
                                {busy ? 'Removing…' : 'Confirm remove'}
                              </button>
                              <button
                                type='button'
                                className='ghost-btn'
                                disabled={busy}
                                onClick={() => setConfirmRemoveId(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className='row-actions'>
                              <button
                                type='button'
                                className='ghost-btn'
                                disabled={busy || busyId !== null}
                                onClick={() => toggleRole(w)}
                                title={
                                  w.role === 'ops_admin'
                                    ? 'Demote to ops_worker'
                                    : 'Promote to ops_admin'
                                }
                              >
                                {busy
                                  ? 'Saving…'
                                  : w.role === 'ops_admin'
                                    ? 'Make worker'
                                    : 'Make admin'}
                              </button>
                              <button
                                type='button'
                                className='ghost-btn danger'
                                disabled={isSelf || busyId !== null}
                                title={
                                  isSelf
                                    ? 'You cannot remove your own account'
                                    : 'Revoke ops access and block login'
                                }
                                onClick={() => {
                                  setRowError(null);
                                  setConfirmRemoveId(w.id);
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {rowError?.id === w.id && (
                        <tr className='row-error-row'>
                          <td colSpan={7}>
                            <div className='form-error' role='alert'>
                              {rowError.message}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PlatformStatsOpsWorkers;
